/**
 * services/telemetry.ts
 *
 * User consent state (localStorage) and lifecycle of the optional Google Analytics and Sentry integrations.
 * Both are off until the user allows them.
 */

import * as Sentry from '@sentry/vue'
import { getApp } from 'firebase/app'
import { type Analytics, getAnalytics, setAnalyticsCollectionEnabled } from 'firebase/analytics'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { type App, ref } from 'vue'
import { logger } from '@/utils/logger'

const ANALYTICS_KEY = 'consent.analytics'
const SENTRY_KEY = 'consent.sentry'
const SENTRY_DSN = 'https://dcf0deab896e35d23cf5a5cc6cce2536@o4510436253958144.ingest.us.sentry.io/4510436255006720'

function readConsent (key: string): boolean | null {
    const value = localStorage.getItem(key)
    if (value === 'granted') {
        return true
    }
    return value === 'denied' ? false : null
}

/** `null` means the user has not answered yet */
export const analyticsConsent = ref<boolean | null>(readConsent(ANALYTICS_KEY))
export const sentryConsent = ref<boolean>(readConsent(SENTRY_KEY) === true)

let analytics: Analytics | null = null
let vueApp: App | null = null
let authUid: string | null = null

/** The Analytics instance, or `null` while Analytics is not allowed */
export function getAnalyticsInstance (): Analytics | null {
    return analytics
}

function applyAnalytics (enabled: boolean) {
    if (enabled) {
        analytics ??= getAnalytics(getApp())
        setAnalyticsCollectionEnabled(analytics, true)
    } else if (analytics) {
        setAnalyticsCollectionEnabled(analytics, false)
    }
}

/** Only the uid is sent to Sentry, never the email */
function syncSentryUser () {
    if (Sentry.isInitialized()) {
        Sentry.setUser(authUid ? { id: authUid } : null)
        logger.log(`Sentry user set: ${authUid ?? 'null'}`)
    }
}

function applySentry (enabled: boolean) {
    if (enabled && !Sentry.isInitialized()) {
        Sentry.init({
            app: vueApp ?? undefined,
            dsn: SENTRY_DSN,
            release: `ff@${__APP_VERSION__}`,
            dataCollection: { userInfo: false },
        })
        logger.log(`Sentry initialized`)
        syncSentryUser()
    } else if (!enabled && Sentry.isInitialized()) {
        void Sentry.close()
    }
}

/** Starts the integrations the user has allowed; call once at startup */
export function initTelemetry (app: App) {
    vueApp = app
    onAuthStateChanged(getAuth(), user => {
        authUid = user?.uid ?? null
        syncSentryUser()
    })
    applyAnalytics(analyticsConsent.value === true)
    applySentry(sentryConsent.value)
}

export function setAnalyticsConsent (enabled: boolean) {
    localStorage.setItem(ANALYTICS_KEY, enabled ? 'granted' : 'denied')
    analyticsConsent.value = enabled
    applyAnalytics(enabled)
}

export function setSentryConsent (enabled: boolean) {
    localStorage.setItem(SENTRY_KEY, enabled ? 'granted' : 'denied')
    sentryConsent.value = enabled
    applySentry(enabled)
}
